import { EventEmitter, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CartItem } from 'models/cartItem';
import { SiteConfiguration } from 'models/site-configuration';
import { BehaviorSubject, Observable } from 'rxjs';
import { Course } from '../../../../../models/course';
import { environment as env, environment } from '../../../../../src/environments/environment';
import { ConfigurationService } from '../configuration/configuration.service';
import { PortalConfigurationService } from '../portal-configuration/portal-configuration.service';
import { RestApiService } from '../rest-api/rest-api.service';

@Injectable({
    providedIn: 'root'
})
export class CoursesService {
    public courses: Observable<Course[]>;
    public course: Observable<Course[]>;
    public event: Observable<CartItem>;
    public events: Observable<CartItem[]>;
    public categories: Observable<Course[]>;
    public lang: SiteConfiguration;
    public portalCountry: string;
    public portalLang: string;
    public portalId: string;
    private _$currentCoursesSubject: BehaviorSubject<Course[]>;
    private _$currentCourseSubject: BehaviorSubject<Course[]>;
    private _$currentEventSubject: BehaviorSubject<CartItem>;
    private _$currentEventsSubject: BehaviorSubject<CartItem[]> = new BehaviorSubject([]);
    private _$coursesCount: BehaviorSubject<number> = new BehaviorSubject(0);
    private _$translations: BehaviorSubject<any> = new BehaviorSubject({});
    private _$courses: BehaviorSubject<any> = new BehaviorSubject(0);
    private _$updatedCourses: BehaviorSubject<any> = new BehaviorSubject([]);
    languageSelected = new EventEmitter<Promise<any>>();

    constructor(
        private _restApiService: RestApiService,
        private _translateService: TranslateService,
        private config: ConfigurationService,
        private _portalConfigurationService: PortalConfigurationService
    ) {
        this._$currentCoursesSubject = new BehaviorSubject<Course[]>(JSON.parse(localStorage.getItem('courses')));
        this.courses = this._$currentCoursesSubject.asObservable();

        this._$currentCourseSubject = new BehaviorSubject<Course[]>(JSON.parse(localStorage.getItem('course')));
        this.course = this._$currentCourseSubject.asObservable();
    }

    public get coursesCount$(): Observable<{}> {
        return this._$coursesCount.asObservable();
    }

    public get courses$(): Observable<{}> {
        return this._$courses.asObservable();
    }

    public get currentEventsSubject$(): Observable<any> {
        return this._$currentEventsSubject.asObservable();
    }

    public get updatedCourses$(): Observable<any> {
        return this._$updatedCourses.asObservable();
    }

    public getAllCourses() {
        const lang = this.config.getSiteFromUrl();
        let languageOfSite = this.config.getCurrentSite();
        languageOfSite = languageOfSite.split('_')[0].toLocaleUpperCase();

        this.lang = lang;
        let _site = lang ? lang.site : 'en_uk';
        if (lang) {
            _site = lang.site === 'en_test' ? 'en_uk' : _site;
        }

        const _languageSite = languageOfSite ? languageOfSite : 'EN';

        if (this.config.isPartnerPage()) {
            // todo: change to portalId when backend is adjusted
            _site = 'de_de';
        }
        const url = `${env.APIEndpoint}api/v1/course?countryCode=${_site}&language=${_languageSite.toLocaleUpperCase()}`;

        return this._restApiService.requestGET<any>(url, null, false, true).then(
            (courses) => {
                this._$coursesCount.next(courses.length);
                this._$currentCoursesSubject.next(courses);
                return courses;
            },
            (error) => {
                return error;
            }
        );
    }

    public getNumberOfCourses() {
        this.lang = this.config.getSiteFromUrl();
        let _site = this.lang ? this.lang.site : 'en_uk';
        if (this.config.isPartnerPage()) {
            // todo: change to portalId when backend is adjusted
            _site = 'de_de';
        }
        const url = `${env.APIEndpoint}api/v1/course/${_site}/count`;
        // const url = `${environment.locales}assets/mock/courses.json`;

        return this._restApiService.requestGET<any>(url, null, false, true).then(
            (numberOfCourses) => {
                return numberOfCourses;
            },
            (error) => {
                return error;
            }
        );
    }

    public getAllPortalCoursesForCourseOverview(portal: string, lang: string) {
        let _url = '';
        if (lang) {
            _url = `${env.APIEndpoint}api/v1/course/${portal}/getportalcourses?language=${lang.toLocaleUpperCase()}`;
        }

        return this._restApiService.requestGET<any>(_url, null, false, true).then(
            (courses) => {
                this._$coursesCount.next(courses.length);
                this._$currentCoursesSubject.next(courses);
                return courses;
            },
            (error) => {
                console.log('error');
                return [];
                // return error;
            }
        );
    }

    public getAllPortalCourses(portal: string) {
        let lang = JSON.parse(sessionStorage.getItem('portalLang'));
        if (lang) {
            lang = lang.lang;
        }
        if (!lang) {
            this._portalConfigurationService.loadPortalPreferences(portal).then((preferences) => {
                lang = this._portalConfigurationService.getPortalLanguage(preferences);
            });
        }
        const currentPortal = this.config.portalId;
        let _url = '';
        if (lang) {
            _url = `${env.APIEndpoint}api/v1/course/${portal}/getportalcourses?language=${lang.toLocaleUpperCase()}`;
        }

        return this._restApiService.requestGET<any>(_url, null, false, true).then(
            (courses) => {
                this._$coursesCount.next(courses.length);
                this._$currentCoursesSubject.next(courses);
                return courses;
            },
            (error) => {
                console.log('error');
                return [];
                // return error;
            }
        );
    }

    public getUserCourses() {
        let lang = 'de';
        const _site = this.config.getCurrentSite();
        lang = _site.split('_')[0];
        let params = '';
        if (this.config.isPartnerPage()) {
            // todo: change to portalId when backend is adjusted
            // _site = 'de_de';

            this.config.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                    const temp = portalId.toLocaleUpperCase().replace(/-/g, '_');
                    params = `?portalName=${temp}`;
                }
            });

            lang = JSON.parse(sessionStorage.getItem('portalLang'))?.lang;
            if (!lang) {
                this._portalConfigurationService.loadPortalPreferences(_site).then((preferences) => {
                    lang = this._portalConfigurationService.getPortalLanguage(preferences);
                });
            }
        } else {
            params = `?countryCode=${_site}`;
        }

        const url = `${env.APIEndpoint}api/v1/booking/mycourses${params}&language=${lang.toLocaleUpperCase()}`;

        return this._restApiService.requestGET<any>(url, null, false, true).then(
            (courses) => {
                this._$coursesCount.next(courses.length);
                this._$currentCoursesSubject.next(courses);
                this._$updatedCourses.next(courses);
                return courses;
            },
            (error) => {
                return error;
            }
        );
    }

    public getCourseById(id: string) {
        this.lang = this.config.getSiteFromUrl();
        let _site = this.lang ? this.lang.site : 'en_uk';
        if (_site === 'en_test') {
            _site = 'en_uk';
        }

        let params = '';
        if (this.config.isPartnerPage()) {
            // todo: change to portalId when backend is adjusted
            // _site = 'de_de';

            this.config.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                    const temp = portalId.toLocaleUpperCase().replace(/-/g, '_');
                    params = `?portalName=${temp}`;
                }
            });
        } else {
            params = `?countryCode=${_site}`;
        }
        const lang = _site.split('_')[0];

        return this._restApiService
            .requestGET<any>(`${env.APIEndpoint}api/v1/course/${id}?language=${lang.toLocaleUpperCase()}&countryCode=${_site}`, null, false, true)
            .then(
                // return this._restApiService.requestGET<any>(`${environment.locales}assets/mock/AAADHEH.json?countryCode=de_de`).then(
                (course) => {
                    this._$currentCourseSubject.next(course);
                    return course;
                },
                (error) => {
                    console.log(error);
                    return error;
                }
            );
    }

    public getBookedCourseById(id: string) {
        this.lang = this.config.getSiteFromUrl();
        let _site = this.lang ? this.lang.site : 'en_uk';
        if (_site === 'en_test') {
            _site = 'en_uk';
        }

        let params = '';
        if (this.config.isPartnerPage()) {
            // todo: change to portalId when backend is adjusted
            // _site = 'de_de';

            this.config.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                    const temp = portalId.toLocaleUpperCase().replace(/-/g, '_');
                    params = `?portalName=${temp}`;
                }
            });
        } else {
            params = `?countryCode=${_site}`;
        }
        const lang = _site.split('_')[0];

        return this._restApiService
            .requestGET<any>(
                `${env.APIEndpoint}api/v1/Course/BookedCourseDetails/${id}?countryCode=${_site}&language=${lang}`,
                null,
                false,
                true
            )
            .then(
                // return this._restApiService.requestGET<any>(`${environment.locales}assets/mock/AAADHEH.json?countryCode=de_de`).then(
                (course) => {
                    this._$currentCourseSubject.next(course);
                    return course;
                },
                (error) => {
                    console.log(error);
                    return error;
                }
            );
    }

    public getPortalCourseById(id: string, portalCountry) {
        let _site = this.config.getCurrentSite();
        let countryCode = JSON.parse(localStorage.getItem('portalCountryCode')).countryCode;
        this.config.currentPortal$.subscribe((portalId) => {
            if (portalId) {
                _site = portalId.replace(/-/g, '_');
            }
        });

        let lang = JSON.parse(sessionStorage.getItem('portalLang'))?.lang;
        if (!lang) {
            this._portalConfigurationService.loadPortalPreferences(_site).then((preferences) => {
                lang = this._portalConfigurationService.getPortalLanguage(preferences);
            });
        }

        if (!countryCode) {
            countryCode = lang + '_' + portalCountry;
        }

        if (countryCode === 'fr_fr') {
            countryCode = 'fr_fra';
        } else if (countryCode === 'en_au') {
            countryCode = 'en_aus';
        }

        return this._restApiService
            .requestGET<any>(
                `${env.APIEndpoint}api/v1/course/${_site.toLocaleUpperCase()}/getportalcourse/${id}?language=${lang.toLocaleUpperCase()}&countryCode=${countryCode}`,
                null,
                false,
                true
            )
            .then(
                (course) => {
                    this._$currentCourseSubject.next(course);
                    return course;
                },
                (error) => {
                    console.log(error);
                    return error;
                }
            );
    }

    public getBookedPortalCourseById(id: string, portalCountry) {
        let countryCode = JSON.parse(localStorage.getItem('portalCountryCode')).countryCode;
        this.config.currentPortal$.subscribe((portalId) => {
            if (portalId) {
                this.portalId = portalId;
                this.portalId = portalId.replace(/-/g, '_');
            }
        });

        let lang = JSON.parse(sessionStorage.getItem('portalLang'))?.lang;
        if (!lang) {
            this._portalConfigurationService.loadPortalPreferences(this.portalId).then((preferences) => {
                lang = this._portalConfigurationService.getPortalLanguage(preferences);
            });
        }

        if (!countryCode) {
            countryCode = lang + '_' + portalCountry;
        }

        if (countryCode === 'fr_fr') {
            countryCode = 'fr_fra';
        } else if (countryCode === 'en_au') {
            countryCode = 'en_aus';
        }
        this.portalId = this.portalId.toLocaleUpperCase();

        return this._restApiService
            .requestGET<any>(
                `${env.APIEndpoint}api/v1/Course/BookedCourseDetails/${id}?portalName=${this.portalId}&language=${lang}`,
                null,
                false,
                true
            )
            .then(
                (course) => {
                    this._$currentCourseSubject.next(course);
                    return course;
                },
                (error) => {
                    console.log(error);
                    return error;
                }
            );
    }

    public getItemsFromCart(cartItems) {
        this.lang = this.config.getSiteFromUrl();

        let _site = this.lang ? this.lang.site : 'en_uk';

        let params = '';
        let cartObj;
        if (this.config.isPartnerPage()) {
            // todo: change to portalId when backend is adjusted
            // _site = 'de_de';
            this.config.currentPortal$.subscribe((portalId) => {
                _site = portalId;
                let countryCode: string;
                if (portalId) {
                    const temp = portalId.toLocaleUpperCase().replace(/-/g, '_');
                    params = `?portalName=${temp}`;
                    if (localStorage.getItem('portalCountryCode')) {
                        countryCode = JSON.parse(localStorage.getItem('portalCountryCode')).countryCode;
                    }
                }
                if (!countryCode) {
                    countryCode = 'de_de';
                }

                if (cartItems && (cartItems.events.length > 0 || cartItems.courses.length > 0 || cartItems.wishlist.length > 0)) {
                    cartObj = {
                        eventIds: cartItems.events,
                        courseIds: cartItems.courses,
                        wishlist: cartItems.wishlist,
                        portalName: _site.toLocaleUpperCase().replace(/-/g, '_'),
                        countryCode: countryCode
                    };
                }
            });
        } else {
            if (cartItems && (cartItems.events.length > 0 || cartItems.courses.length > 0 || cartItems.wishlist.length > 0)) {
                cartObj = {
                    eventIds: cartItems.events,
                    courseIds: cartItems.courses,
                    wishlist: cartItems.wishlist,
                    countryCode: _site
                };
            }
        }

        const _url = `${env.APIEndpoint}api/v1/booking/findcourseforcart`;

        return this._restApiService.requestRawBodyPOST<any>(_url, cartObj, null, true, true).then(
            (data) => {
                if (data) {
                    this._$coursesCount.next(data.length);
                }

                this._$currentEventsSubject.next(data);
                return data;
            },
            (error) => {
                console.log('error');
                return error;
            }
        );
    }

    public getEventDetails(eventId) {
        this.lang = this.config.getSiteFromUrl();

        const _site = this.lang ? this.lang.site : 'en_uk';

        let params;
        if (this.config.isPartnerPage()) {
            // todo: change to portalId when backend is adjusted
            // _site = 'de_de';

            this.config.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                    const temp = portalId.toLocaleUpperCase().replace(/-/g, '_');
                    params = `?portalName=${temp}`;
                }
            });
        } else {
            params = `?countryCode=${_site}`;
        }

        const _url = `${env.APIEndpoint}api/v1/course/${eventId}/geteventdetail${params}`;

        return this._restApiService.requestGET<any>(_url).then(
            (data) => {
                if (data) {
                    this._$coursesCount.next(data.length);
                }

                this._$currentEventsSubject.next(data);
                return data;
            },
            (error) => {
                console.log('error');
                return error;
            }
        );
    }


    public removeItem(courses: CartItem[], course): Promise<CartItem[]> {
        const _courses = courses.filter((courseItem) => course.id !== courseItem.id);
        return new Promise<CartItem[]>((resolve) => {
            resolve(_courses);
        });
    }

    public getBookingDetails(id: string) {
        const url = `${env.APIEndpoint}api/v1/booking/${id}`;

        return this._restApiService.requestGET<any>(url, null, false, true).then(
            (booking) => {
                return booking;
            },
            (error) => {
                console.log(error);
            }
        );
    }

    public getSimilarCourses(courseId) {
        this.lang = this.config.getSiteFromUrl();
        let _site = this.lang ? this.lang.site : 'en_uk';
        if (this.config.isPartnerPage()) {
            // todo: change to portalId when backend is adjusted
            _site = 'de_de';
        }

        return this._restApiService.requestGET<any>(`${env.APIEndpoint}api/v1/course/${courseId}/recommendation?countryCode=${_site}`).then(
            (courses) => {
                return courses;
            },
            (error) => {
                console.log(error);
                return error;
            }
        );
    } 

    getCurrencies(course, site) {
        if (course.prices.length > 0) {
            return course.prices[0]?.currency;
        } else if (site === 'en_au' || site === 'en_nz') {
            return '$';
         } else if (site === 'en_uk') {
            return '£';
        } else {
            return '€';
        }
    }
}
